export const MemexWorkflowTriggerType = {
  ItemAdded: 'item_added',
  Reopened: 'reopened',
  ReviewChangesRequested: 'review_changes_requested',
  ReviewApproved: 'review_approved',
  Closed: 'closed',
  Merged: 'merged',
  QueryMatched: 'query_matched',
  ProjectItemColumnUpdate: 'project_item_column_update',
  SubIssues: 'sub_issues',
  PrConnected: 'pr_connected',
  AutoAssignAgent: 'auto_assign_agent',
} as const
export type MemexWorkflowTriggerType = ObjectValues<typeof MemexWorkflowTriggerType>

export const MemexActionType = {
  SetField: 'set_field',
  GetItems: 'get_items',
  GetProjectItems: 'get_project_items',
  ArchiveProjectItem: 'archive_project_item',
  AddProjectItem: 'add_project_item',
  CloseItem: 'close_item',
  GetSubIssues: 'get_sub_issues',
  AssignCopilot: 'assign_copilot',
} as const
export type MemexActionType = ObjectValues<typeof MemexActionType>

export const MemexWorkflowActionPriority: Record<MemexActionType, number> = {
  get_project_items: 0,
  get_items: 0,
  get_sub_issues: 0,
  add_project_item: 1,
  archive_project_item: 1,
  close_item: 2,
  set_field: 2,
  assign_copilot: 2,
}

export type MemexWorkflowType = `${MemexWorkflowTriggerType}_${MemexActionType}`

export type MemexWorkflowId = number | MemexWorkflowType

export interface CreateWorkflowRequest {
  workflow: Omit<MemexWorkflow, 'id' | 'actions'> & {actions: Array<Omit<MemexWorkflowAction, 'id'>>}
}

export interface CreateWorkflowResponse {
  workflow: MemexWorkflowPersisted
}

export type UpdateWorkflowRequestChanges = Partial<
  Pick<MemexWorkflowPersisted, 'name' | 'enabled' | 'contentTypes' | 'actions'>
>
export type CreateWorkflowRequestChanges = Partial<Pick<MemexWorkflow, 'name' | 'enabled' | 'contentTypes' | 'actions'>>
export interface UpdateWorkflowRequest extends UpdateWorkflowRequestChanges {
  workflowNumber: number
}

export type UpdateWorkflowResponse = CreateWorkflowResponse

export interface DeleteWorkflowRequest {
  workflowNumber: number
}

export const MemexWorkflowContentType = {
  Issue: 'Issue',
  PullRequest: 'PullRequest',
} as const
export type MemexWorkflowContentType = ObjectValues<typeof MemexWorkflowContentType>

export interface MemexWorkflow {
  id?: MemexWorkflowId
  name: string
  triggerType: MemexWorkflowTriggerType
  contentTypes: Array<MemexWorkflowContentType>
  enabled: boolean
  actions: Array<MemexWorkflowAction>
  notices?: Array<MemexWorkflowNotice>
}

export type ClientMemexWorkflow = MemexWorkflow & {
  clientId: string
  clientIdOriginal: string // ID to track active workflow before/after creation on server
  isUserWorkflow: boolean
}

export type MemexWorkflowPersisted = MemexWorkflow & {
  id: number
  number: number
  actions: Array<MemexWorkflowActionPersisted>
}

interface SetFieldActionArguments {
  fieldId: number
  fieldOptionId: string
}

interface GetProjectItemsActionArguments {
  query: string
  fieldId?: number
  fieldOptionId?: string
}

interface GetItemsActionArguments {
  query: string
  repositoryId?: number
}

interface AddProjectItemActionArguments {
  repositoryId?: number
  subIssue?: boolean
}

export interface AssignCopilotActionArguments {
  instruction?: string
  destinationFieldOptionId?: string
  outputMode?: string
  model?: string
  agentId?: string
  allowedRepositories?: Array<string>
}

export interface MemexWorkflowAction {
  id?: number
  actionType: MemexActionType
  arguments: Partial<
    SetFieldActionArguments &
      GetProjectItemsActionArguments &
      GetItemsActionArguments &
      AddProjectItemActionArguments &
      AssignCopilotActionArguments
  >
}

export type MemexWorkflowActionPersisted = MemexWorkflowAction & {
  id: number
}

export const MemexWorkflowNoticeType = {
  ConfiguredModelUnavailable: 'configured_model_unavailable',
} as const
export type MemexWorkflowNoticeType = ObjectValues<typeof MemexWorkflowNoticeType>

export interface ConfiguredModelUnavailableWorkflowNotice {
  type: typeof MemexWorkflowNoticeType.ConfiguredModelUnavailable
  message: string
  model: string
}

export type UnknownMemexWorkflowNotice = Record<string, unknown>

export type MemexWorkflowNotice = ConfiguredModelUnavailableWorkflowNotice | UnknownMemexWorkflowNotice

export interface MemexWorkflowConfiguration {
  triggerType: MemexWorkflowTriggerType
  enableable: boolean
  constraints: {contentTypes: Array<MemexWorkflowContentType>}
  defaultWorkflow: MemexWorkflow
  /**
   * Optional id of the field option recommended by the backend for this
   * workflow's source column. Currently only set on the auto-assign-agent
   * workflow configuration.
   */
  recommendedFieldOptionId?: string
}

export interface UpdateMemexWorkflowEnabledRequest {
  workflow: ClientMemexWorkflow
  enable: boolean
}

export type MemexWorkflowMenuState = {
  [key: string]: boolean
}
